import { Badge, Button } from '@mdxui/primitives'
import { cn } from '@mdxui/primitives/lib/utils'
import { ChevronRight } from 'lucide-react'
import type { ComponentType, ReactNode } from 'react'
import type { BlogPost } from './blog-card'
import { formatDate as defaultFormatDate } from '../lib/utils'

export interface BlogGridLinkProps {
  href: string
  children: ReactNode
  className?: string
}

export interface BlogGridProps<T> {
  /** Array of blog items */
  items: T[]
  /** Render function for each blog card */
  renderItem: (item: T, index: number) => ReactNode
  /** View mode: 'card' for grid, 'list' for list */
  viewMode?: 'card' | 'list'
  /** Format date function (for list view) */
  formatDate?: (date: string) => string
  /** Custom link component for framework routing (for list view) */
  LinkComponent?: ComponentType<BlogGridLinkProps>
  /** Base path for post links (default: '') — e.g. '/blog' produces '/blog/{slug}' */
  basePath?: string
  /** Additional className */
  className?: string
}

/**
 * Default link component (anchor tag)
 */
function DefaultLink({ href, children, className }: BlogGridLinkProps) {
  return (
    <a href={href} className={className}>
      {children}
    </a>
  )
}

/**
 * List view row for a blog post
 */
function BlogListRow({
  post,
  formatDate = defaultFormatDate,
  LinkComponent = DefaultLink,
  basePath = '',
}: {
  post: BlogPost
  formatDate?: (date: string) => string
  LinkComponent?: ComponentType<BlogGridLinkProps>
  basePath?: string
}) {
  const href = `${basePath}/${post.slug}`
  const displayTags = post.tags?.slice(0, 2) || []
  const extraTagCount = (post.tags?.length || 0) - 2

  return (
    <LinkComponent href={href} className="group block py-3 hover:bg-muted/50 transition-colors px-2">
      <div className="grid grid-cols-[100px_1fr] gap-14">
        {/* Date - fixed width column */}
        <span className="text-xs text-muted-foreground font-mono whitespace-nowrap pt-0.5">
          {post.date ? formatDate(post.date) : ''}
        </span>

        {/* Content - stacked */}
        <div className="space-y-1 min-w-0">
          {/* Title */}
          <div className="font-semibold">
            {post.title}
          </div>

          {/* Description + Badges + Read */}
          <div className="flex items-center justify-between gap-4">
            {/* Description */}
            <span className="flex-1 min-w-0 text-sm text-muted-foreground truncate">
              {post.description}
            </span>

            {/* Badges + Read button */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className="flex gap-1">
                {displayTags.map((tag) => (
                  <Badge key={tag} variant="secondary">
                    {tag}
                  </Badge>
                ))}
                {extraTagCount > 0 && (
                  <Badge variant="secondary">+{extraTagCount}</Badge>
                )}
              </div>

              <Button variant="link" className="text-muted-foreground group-hover:text-foreground hover:no-underline transition-colors">
                Read <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </LinkComponent>
  )
}

export function BlogGrid<T>({
  items,
  renderItem,
  viewMode = 'card',
  formatDate,
  LinkComponent,
  basePath,
  className,
}: BlogGridProps<T>) {
  return (
    <section className={cn('w-full px-6 pb-16 sm:pb-20', className)}>
      {/* Card view: always shown on mobile, shown on md+ when viewMode is 'card' */}
      <div className={cn(
        'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6',
        viewMode === 'list' ? 'md:hidden' : ''
      )}>
        {items.map((item, index) => renderItem(item, index))}
      </div>

      {/* List view: hidden on mobile, shown on md+ when viewMode is 'list' */}
      {viewMode === 'list' && (
        <div className="hidden md:block divide-y divide-border">
          {items.map((item, index) => (
            <BlogListRow
              key={String(index)}
              post={item as unknown as BlogPost}
              formatDate={formatDate}
              LinkComponent={LinkComponent}
              basePath={basePath}
            />
          ))}
        </div>
      )}
    </section>
  )
}
